import { z } from 'zod';
import {
  createModel,
  currencySchema,
  imageWithUploadSchema,
  paymentGatewaySchema,
  QueryMany,
  QueryOne,
  refSchema,
} from '../../common';
import { dateSchema, nameSchema, objectIdSchema, priceSchema } from '../../validation';
import { TransactionStatusSchema } from '../transaction/transaction.status.model';
import { transactionFlowSchema } from '../transaction/transaction.flow.model';
import { transactionInvoiceSchema } from '../transaction/transaction.invoice.model';
import { UTagSharedSchema } from '../utag';
import { AccountTransactionTypeSchema } from './account-transaction.type.model';
import { accountTransactionAccountSchema } from './account-transaction.account.model';

/**
 * `AccountTransaction db model`
 *
 * 90% mirror of {@link TransactionSchema}. Same {@link TransactionStatusSchema},
 * signed `priceSchema` `amount`, `creditAmount`/`debitAmount`,
 * `gateway`, `images`, `notes`, approve/reject metadata, `UTags`.
 * Reuses {@link transactionInvoiceSchema} verbatim for the
 * `invoices[]` array so multi-invoice settles and partial payments
 * keep working without re-implementing the per-invoice delta math.
 *
 * The 10% that's different:
 * 1. `account` (snapshot) instead of `wallet` — embeds the full
 *    `partySchema` tuple instead of flattening to
 *    `holderId`/`holderName`/`holderType`.
 * 2. `type` is the new {@link AccountTransactionTypeSchema} —
 *    leg-aware, role-neutral.
 * 3. `order` nested snapshot — gives the UI a display ref
 *    ("Order #ABC-1234") without a second fetch.
 *
 * Crucially: NO writes to the legacy `Wallet` or `Transaction`
 * collections. New flows read and write only `Account` and
 * `AccountTransaction`. The legacy collections stay frozen, parked
 * for the future personal-wallet-cards feature.
 */
export const AccountTransactionSchema = createModel({
  /**
   * - `pending` = posted but not settled (e.g. gateway-initiated, awaiting webhook).
   * - `waiting_for_approval` = wire transfer awaiting finance team verification.
   * - `success` = the transaction is settled; Account balance has moved.
   * - `failed` = something wrong; no Account balance impact.
   */
  status: TransactionStatusSchema,

  /** the currency of the transaction */
  currency: currencySchema,
  /** transaction ref (main & short) */
  ref: refSchema,

  /**
   * The signed move applied to `Account.balance` from `parties[0]`'s POV.
   * Can be positive (parties[0]'s receivable grew) or negative
   * (parties[0]'s payable grew). The sign is determined at post-time
   * by `AccountDTOService.signForSeller(account, sellerId)` for
   * leg-tied transactions.
   */
  amount: priceSchema,

  /**
   * How much of `amount` was funded from the account's existing
   * credit (advance balance applied). Mirrors `Transaction.creditAmount`.
   */
  creditAmount: priceSchema,

  /**
   * How much of `amount` was new money moved via the gateway / cash /
   * bank. Mirrors `Transaction.debitAmount`.
   */
  debitAmount: priceSchema,

  /** when the transaction was created */
  createdAt: dateSchema,

  /** see {@link AccountTransactionTypeSchema} */
  type: AccountTransactionTypeSchema,

  /**
   * the payment gateway used for any actual money movement. Optional
   * because some types (e.g. `leg_capture`, `leg_void`,
   * `leg_adjustment`) don't move money — they just adjust the
   * Account balance.
   */
  gateway: paymentGatewaySchema.optional(),

  /** any images or proof-of-payment attachments */
  images: z.array(imageWithUploadSchema),

  /** free-text notes (admin / finance team comments) */
  notes: nameSchema.optional(),

  /**
   * The direction of the balance move on the Account.
   * - `+1` = balance moves UP (parties[0]'s receivable grew).
   * - `-1` = balance moves DOWN (parties[0]'s payable grew).
   *
   * Provided alongside the signed `amount` so consumers reading the
   * tx in isolation don't have to know about the canonical-ordering
   * sign convention to render direction badges. For leg-tied txs
   * the sign of `amount` and `flow` always agree.
   */
  flow: transactionFlowSchema,

  /**
   * Snapshot of the Account at post-time, with balance set to the
   * value AFTER this transaction has been applied.
   */
  account: accountTransactionAccountSchema,

  /**
   * Per-invoice deltas. ONE AccountTransaction can settle 0..N
   * invoices in one shot (e.g. "Pay all due" against a
   * counterparty). Reuses {@link transactionInvoiceSchema} VERBATIM
   * so partial payment / multi-invoice settle / return / unused
   * advance math all work without re-implementation.
   *
   * `0` for direct ledger moves like `account_debit` / `cashback`
   * that aren't tied to specific invoices.
   */
  invoices: z.array(transactionInvoiceSchema).min(0),

  /**
   * Per-leg backlink as a nested snapshot. Carries the order's
   * display `ref` alongside the ids so the transactions tab can
   * show "Order #ABC-1234 (leg 2)" without a second fetch.
   *
   * Set when the tx is tied to exactly one leg (the common case:
   * `leg_capture`, `leg_void`, `leg_adjustment`). For multi-invoice
   * payments that span legs (rare, but possible via "Pay all due"),
   * this stays `undefined` — the per-invoice entries in
   * `invoices[]` carry the per-leg detail via each invoice's items.
   */
  order: z
    .object({
      /** the Order `_id` */
      _id: objectIdSchema,
      /** the `Order.contracts[i].contractId` this tx settles */
      contractId: objectIdSchema,
      /** Order ref (main + short) for display */
      ref: refSchema,
    })
    .optional(),

  /** Date when the transaction was approved by finance team */
  approvedAt: dateSchema.optional(),
  /** Identifier of who approved the transaction */
  approvedBy: nameSchema.optional(),
  /** Reason for rejecting the transaction */
  rejectionReason: nameSchema.optional(),
  /** Date when the transaction was rejected */
  rejectedAt: dateSchema.optional(),
  /** Identifier of who rejected the transaction */
  rejectedBy: nameSchema.optional(),

  UTags: z.array(UTagSharedSchema),
});

/** `AccountTransaction db model` */
export type AccountTransaction = z.infer<typeof AccountTransactionSchema.model>;
export type AccountTransactionData = z.infer<typeof AccountTransactionSchema.data>;
export type AccountTransactionPartial = z.infer<typeof AccountTransactionSchema.partial>;
export type AccountTransactionQueryOne = QueryOne<AccountTransaction>;
export type AccountTransactionQueryMany = QueryMany<AccountTransaction>;
